import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket, faCheck, faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
    useEventIngestChannelTrigger,
    useEventIngestImageUpload,
    useEventIngestMetadataUpload, useEventIngestTrigger,
} from "../../../../../services/epg/Query";

export type ChannelNameWithPipelineName = {
    channelName: string;
    pipelineName: string;
    uuid: string
}

type IngestTriggerCardProps = {
    channels?: ChannelNameWithPipelineName[],
    heading: string;
    subHeading: string;
    ingestType: {
        label: "ALL_AUTOMATED" | "SPECIFIC_AUTOMATED" | "METADATA_MANUAL" | "IMAGES_MANUAL",
    }
}

const IngestTriggerCard = ({ channels, heading, subHeading, ingestType } : IngestTriggerCardProps) => {
    const imageIngestUpload = useEventIngestImageUpload();
    const metadataIngestUpload = useEventIngestMetadataUpload();
    const specificChannelIngest = useEventIngestChannelTrigger();
    const allChannelsIngest = useEventIngestTrigger();

    const { label } = ingestType;
    const [ file, setFile ] = useState<File | null>(null);
    const [ showChannels, setShowChannels ] = useState(false);
    const [ selectedChannel, setSelectedChannel ] = useState<ChannelNameWithPipelineName | undefined>();
    const [ disableTriggerButton, setDisableTriggerButton ] = useState(!(label === "ALL_AUTOMATED"));
    const [ showFeedbackMessage, setShowFeedbackMessage ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const dropDownRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const isPending = imageIngestUpload.isPending || metadataIngestUpload.isPending
            || specificChannelIngest.isPending || allChannelsIngest.isPending;

        setIsLoading(isPending);
    }, [
        imageIngestUpload.isPending,
        metadataIngestUpload.isPending,
        specificChannelIngest.isPending,
        allChannelsIngest.isPending,
    ]);

    useEffect(() => {
        const isSuccess = imageIngestUpload.isSuccess || metadataIngestUpload.isSuccess
            || specificChannelIngest.isSuccess || allChannelsIngest.isSuccess;
        const isError = imageIngestUpload.isError || metadataIngestUpload.isError
            || specificChannelIngest.isError || allChannelsIngest.isError;

        setIsSuccess(isSuccess);

        if (isError || isSuccess) {
            setShowFeedbackMessage(true);
            setTimeout(() => {
                setShowFeedbackMessage(false);
            }, 5000);
        }
    }, [
        imageIngestUpload.isSuccess,
        metadataIngestUpload.isSuccess,
        specificChannelIngest.isSuccess,
        allChannelsIngest.isSuccess,
        imageIngestUpload.isError,
        metadataIngestUpload.isError,
        specificChannelIngest.isError,
        allChannelsIngest.isError,
    ]);

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (dropDownRef.current) {
                if (dropDownRef.current && !dropDownRef.current.contains(e.target as Node)) {
                    setShowChannels(false);
                }
            }
        };

        if (showChannels)
            document.addEventListener("mousedown", handleOutsideClick);
        else document.removeEventListener("mousedown", handleOutsideClick);

        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [ showChannels ]);

    useEffect(() => {
        if (label === "ALL_AUTOMATED") {
            setDisableTriggerButton(false);
        } else if (label === "SPECIFIC_AUTOMATED"){
            setDisableTriggerButton(!selectedChannel);
        } else {
            setDisableTriggerButton(!(selectedChannel && file));
        }
    }, [ label, selectedChannel, file ]);

    const selectChannel = (channel: ChannelNameWithPipelineName) => {
        setSelectedChannel(channel);
    };

    const submit = async () => {
        const { uuid = "", pipelineName = "" } = selectedChannel ?? {};

        if (label === "METADATA_MANUAL" || label === "IMAGES_MANUAL") {
            if (!selectedChannel || !fileInputRef.current?.files) return;

            const file = fileInputRef.current?.files?.[0];
            if (label === "IMAGES_MANUAL"){
                await imageIngestUpload.mutateAsync({ channelUuid: uuid, file: file as File });
            } else {
                await metadataIngestUpload.mutateAsync({ pipelineName: pipelineName, file: file as File });
            }
        } else if (label === "SPECIFIC_AUTOMATED"){
            if(!selectedChannel) return;

            await specificChannelIngest.mutateAsync(pipelineName);
        } else if (label === "ALL_AUTOMATED") {
            await allChannelsIngest.mutateAsync();
        }

        setSelectedChannel(undefined);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFileChange = (e:  React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && files.length > 0) setFile(files[0]);
        else setFile(null);
    };

    return (

        <div className = "flex flex-col w-full h-full bg-white dark:!bg-brand-700 shadow-xl dark:text-white rounded-xl p-4">
            <div className = {`pb-5`}>
                <p className = {`text-2xl font-medium`}>{heading}</p>
                <p className = {`text-gray-500`}>{subHeading}</p>
            </div>
            {label !== "ALL_AUTOMATED" && (
                <div
                    ref = {dropDownRef}
                    className = {`flex gap-2 mb-4 flex-col w-full relative overflow-visible`}
                >
                    <p className = {`font-semibold`}>Select a Channel</p>
                    <div
                        onClick = {() => {
                            setShowChannels((prev) => !prev);
                        }}
                        className = {`flex flex-row items-center bg-gray-100 rounded-lg p-2 justify-between dark:bg-gray-300 dark:text-gray-900 cursor-pointer`}
                    >
                        <p>{selectedChannel === undefined ? "Choose a channel" : selectedChannel.channelName}</p>
                        <FontAwesomeIcon
                            icon = {faChevronDown}
                            size = "xs" fixedWidth
                            className = {`dark:text-gray-900`}
                        />
                        {channels && channels.length > 0 &&
                            (
                                <div className = {`${showChannels ? "block" : "hidden"} overflow-hidden rounded-lg 
                                    w-full absolute left-0 top-full mt-2 border border-gray-100/10 shadow-xl z-10`}
                                >
                                    <div className = {`flex flex-col w-full max-h-48 bg-white dark:!bg-brand-700 p-2 
                                        gap-2 overflow-auto`}
                                    >
                                        {
                                            channels.map((channel) => (
                                                <div
                                                    key = {channel.uuid}
                                                    onClick = {() => selectChannel(channel)}
                                                    className = {`group px-4 py-1 rounded-lg cursor-pointer flex flex-row 
                                                        gap-2 items-center hover:bg-blue-800 hover:dark:bg-indigo-400 
                                                        hover:text-white`}
                                                >
                                                    <FontAwesomeIcon
                                                        icon = {faCheck}
                                                        className = {`group-hover:text-white dark:text-white text-gray-900 
                                                            ${selectedChannel && selectedChannel.uuid === channel.uuid
                                                            ? "visible" : "invisible"}`}
                                                    />
                                                    <p className = {`dark:text-white`}>{channel.channelName}</p>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            )}
            {(label === "METADATA_MANUAL" || label === "IMAGES_MANUAL") && (
                <div className = {`flex gap-2 mb-4 flex-col w-full`}>
                    <label htmlFor = {"file"} className = {`font-semibold`}>File Upload</label>
                    <input
                        type = {`file`}
                        id = {`file`}
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        disabled={selectedChannel === undefined}
                        className={`file:bg-gray-100 dark:file:bg-gray-300 file:border-0 file:font-semibold bg-gray-100 
                            rounded-lg p-2 dark:bg-gray-300 dark:text-gray-900 disabled:dark:text-gray-300/10 
                            cursor-pointer`}
                        accept = {`.zip,.csv,.xml`}
                    />
                </div>
            )}
            <div>
                <button
                    onClick={submit}
                    disabled = {disableTriggerButton}
                    className = {`flex flex-row py-2 w-28 px-2 items-center gap-2 bg-blue-800 dark:bg-indigo-400
                     disabled:bg-gray-400 dark:disabled:bg-gray-400 shadow-md border p-1 rounded-lg 
                     disabled:text-gray-800 disabled:border-gray-700 text-white`}
                >
                    <FontAwesomeIcon
                        icon = {isLoading ? faSpinner : faArrowUpFromBracket}
                        size = "1x" fixedWidth
                        className = {`${disableTriggerButton ? "text-gray-800" : "text-white"}`}
                    />
                    <p className = {`font-semibold`}>{isLoading ? "Loading..." : "Trigger"}</p>
                </button>
                <p className={`mt-4 ${isSuccess ? "text-green-300" : "text-red-300"} font-semibold 
                    ${showFeedbackMessage ? "block" : "hidden"}`}
                >
                    {isSuccess ? `Event successfully triggered.` : `Error triggering event.`}
                </p>
            </div>
        </div>
    );
};

export default IngestTriggerCard;